import { useQueryClient } from "@tanstack/react-query";
import { useAssistantRuntime, useAui, useAuiState } from "@assistant-ui/react";
import { type FC, useCallback, useContext, useEffect, useRef } from "react";

import { abandonActiveChatTurn } from "@/api/chat/chat";
import type {
    IChatRecoveryStatus,
    RecoveryDisplay,
} from "@/api/chat/chat.types";
import { getAuthSessionEpoch } from "@/contexts/auth/AuthContext";
import { useAuth } from "@/contexts/auth/useAuth";
import { OrganizationContext } from "@/contexts/frontend/organization/OrganizationContext";
import { useMessageRecoveryStatus } from "@/hooks/chat/useMessageRecoveryStatus";
import {
    forgetActiveTurnIf,
    rememberActiveTurn,
    type CapturedTurnIdentity,
} from "@/utils/active-turn-identity";
import {
    useTurnAttachment,
    type FrozenTurnIdentity,
    type TurnAttachment,
} from "@/hooks/chat/useTurnAttachment";

import { useRetryTarget } from "../contexts/RetryContext";

import { stageRecoveredThreadTitle } from "../adapters/api-thread-list-adapter";
import {
    handleChatTurnCompleted,
    loadRecoveredConversationImport,
    waitForRecoveredConversationTitle,
} from "../utils/handleChatTurnCompleted";
import { startRecoveryRetry } from "../utils/startRecoveryRetry";
import { type DocumentWaitCancelOutcome } from "./DocumentWaitCancelButton";
import { isDocumentWait } from "./document-wait";
import { RecoveringMessage } from "./RecoveringMessage";

/**
 * Binds one assistant message's recovering affordance to the status resource.
 *
 * Everything it needs comes from the message's own scope, so it never reaches
 * into thread-list state from inside a message. On any terminal it re-reads the
 * conversation through `thread.import` -- an explicit runtime call rather than a
 * remount, so composer drafts and scroll position survive the swap.
 */
export const RecoveringMessageSlot: FC = () => {
    const aui = useAui();
    const assistantRuntime = useAssistantRuntime();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const organizationId =
        useContext(OrganizationContext)?.organization?.id ?? null;
    const userId = user?.id ?? null;
    const currentIdentityRef = useRef({
        userId,
        organizationId,
        generation: 0,
    });
    if (
        currentIdentityRef.current.userId !== userId ||
        currentIdentityRef.current.organizationId !== organizationId
    ) {
        currentIdentityRef.current = {
            userId,
            organizationId,
            generation: currentIdentityRef.current.generation + 1,
        };
    }

    const recoveryDisplay = useAuiState(
        (s) =>
            s.message.metadata?.custom?.recovery_display as
                | RecoveryDisplay
                | undefined
    );
    const conversationId = useAuiState(
        (s) => s.message.metadata?.custom?.conversation_id as number | undefined
    );
    // The imported row's own claim that it is parked. Only ever consulted
    // before the first status read; the poll is the authority after that.
    const waitingHint = useAuiState(
        (s) =>
            s.message.metadata?.custom?.waiting_for_documents as
                | boolean
                | undefined
    );
    const messageId = useAuiState((s) => Number(s.message.id));
    const branchId = useAuiState((s) => s.message.id);
    // assistant-ui's id for the user turn this answer belongs to. Retry branches
    // from it, which is what makes the replaced answer a sibling branch locally
    // instead of leaving a stale message beside the fresh one.
    const parentId = useAuiState((s) => s.message.parentId);
    const threadListItemId = useAuiState((s) => s.threadListItem.id);
    const retryTarget = useRetryTarget();

    const reloadedRef = useRef(false);
    const reloadPromiseRef = useRef<Promise<string | undefined> | null>(null);
    // Assigned after the attachment hook runs, which cannot be moved above the
    // poll that feeds it.
    const quiesceRef = useRef<TurnAttachment["quiesce"] | null>(null);
    const arrivedRecoveringRef = useRef(false);
    if (recoveryDisplay === "recovering") {
        arrivedRecoveringRef.current = true;
    }
    const mountedRef = useRef(true);
    // The body must re-arm the flag: StrictMode's simulated remount runs the
    // cleanup and then re-runs the effect on the same instance, so a
    // cleanup-only effect leaves the guard latched false and every import
    // silently skipped.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Terminal rows are read too, so the SERVER can authorise Retry on a failed
    // or superseded turn. Terminal statuses stop polling after one read.
    const showsAffordance =
        (recoveryDisplay === "recovering" || recoveryDisplay === "terminal") &&
        conversationId !== undefined &&
        Number.isFinite(messageId);

    // Captured once, before anything can attach. Resuming a run rebinds this
    // index-keyed row to a fresh empty sibling carrying no custom metadata, so
    // every selector above reads that sibling from then on -- no conversation
    // id, no recovery display, a different message id. A poll or an import
    // keyed on them stops dead exactly when the attachment starts.
    const frozenRef = useRef<FrozenTurnIdentity | null>(null);
    // Frozen WITH the target, because the registry entry belongs to the
    // identity that created it. Re-deriving it later would hand this turn to
    // whoever is signed in by then.
    const ownerRef = useRef<CapturedTurnIdentity | null>(null);
    const ownerIdentityGenerationRef = useRef<number | null>(null);
    if (
        frozenRef.current === null &&
        showsAffordance &&
        userId !== null &&
        organizationId !== null
    ) {
        frozenRef.current = {
            conversationId: conversationId as number,
            messageId,
            originalBranchId: branchId,
            parentId,
            threadListItemId,
        };
        ownerRef.current = {
            userId,
            organizationId,
            conversationId: conversationId as number,
            authSessionEpoch: getAuthSessionEpoch(),
        };
        ownerIdentityGenerationRef.current =
            currentIdentityRef.current.generation;
    }
    const target = frozenRef.current;
    const owner = ownerRef.current;
    const ownerIdentityGeneration = ownerIdentityGenerationRef.current;
    const identityGeneration = currentIdentityRef.current.generation;
    const ownerMatchesCurrentIdentity = useCallback(() => {
        return (
            owner !== null &&
            userId !== null &&
            organizationId !== null &&
            ownerIdentityGeneration === identityGeneration &&
            identityGeneration === currentIdentityRef.current.generation &&
            owner.userId === userId &&
            owner.organizationId === organizationId &&
            owner.authSessionEpoch === getAuthSessionEpoch()
        );
    }, [
        identityGeneration,
        organizationId,
        owner,
        ownerIdentityGeneration,
        userId,
    ]);

    const reloadThread = useCallback(
        async (throwOnFailure = false) => {
            if (target === null || !ownerMatchesCurrentIdentity()) return;
            let reload = reloadPromiseRef.current;
            if (reload === null) {
                if (reloadedRef.current) return;
                reloadedRef.current = true;
                reload = (async () => {
                    const { repository, title } =
                        await loadRecoveredConversationImport(
                            target.conversationId
                        );
                    if (!ownerMatchesCurrentIdentity()) return;
                    await quiesceRef.current?.();
                    if (!ownerMatchesCurrentIdentity()) return;
                    if (mountedRef.current) {
                        assistantRuntime.threads
                            .getById(target.threadListItemId)
                            .import(repository);
                    }
                    return title;
                })();
                reloadPromiseRef.current = reload;
            }
            try {
                return await reload;
            } catch (error) {
                reloadedRef.current = false;
                if (throwOnFailure) throw error;
            } finally {
                if (reloadPromiseRef.current === reload) {
                    reloadPromiseRef.current = null;
                }
            }
        },
        [assistantRuntime, ownerMatchesCurrentIdentity, target]
    );

    const enabled = target !== null && ownerMatchesCurrentIdentity();

    const recovery = useMessageRecoveryStatus({
        conversationId: target?.conversationId ?? 0,
        messageId: target?.messageId ?? messageId,
        enabled,
        onTerminal: (status: IChatRecoveryStatus) => {
            if (
                !arrivedRecoveringRef.current ||
                !ownerMatchesCurrentIdentity()
            ) {
                return;
            }
            if (status.status === "completed") {
                handleChatTurnCompleted(queryClient);
            }
            // Failures re-read too. Without this the message keeps the
            // `running` status it loaded with, and assistant-ui's MessageError
            // only renders for `incomplete`/`error` -- so a failed recovery
            // would show no answer, no error and no way forward.
            void reloadThread().then(async (title) => {
                if (
                    status.status === "completed" &&
                    ownerMatchesCurrentIdentity()
                ) {
                    if (title !== undefined) {
                        const remoteId = String(target?.conversationId);
                        const recoveredTitle =
                            await waitForRecoveredConversationTitle(
                                remoteId,
                                title
                            );
                        if (
                            recoveredTitle === undefined ||
                            !ownerMatchesCurrentIdentity()
                        ) {
                            return;
                        }
                        const clearStagedTitle = stageRecoveredThreadTitle(
                            remoteId,
                            recoveredTitle
                        );
                        try {
                            await aui
                                .threads()
                                .item({ id: threadListItemId })
                                .generateTitle();
                        } catch (error) {
                            console.warn(
                                "Recovered conversation title could not be refreshed",
                                error
                            );
                        } finally {
                            clearStagedTitle();
                        }
                    }
                }
            });
        },
        onGone: () => {
            if (!ownerMatchesCurrentIdentity()) return;
            void reloadThread();
        },
    });

    // Stop is composer-scoped and cannot see this row, but `/abandon` names the
    // assistant message and this is the only place a turn the tab did not start
    // is identified -- it is the id the status poll above already uses.
    const turnStillRunning =
        recovery.statusKind === "pending" ||
        recovery.statusKind === "active" ||
        recovery.statusKind === "horizon";
    useEffect(() => {
        if (target === null || owner === null) return;
        const messageId = target.messageId;
        // Once the principal, organization, or session moves, this row releases
        // exactly the entry it created and writes nothing under the scope that
        // replaced it: a Stop over there would otherwise name this message and
        // cancel its own live turn when the tenant fence refuses the abandon.
        if (!turnStillRunning || !ownerMatchesCurrentIdentity()) {
            forgetActiveTurnIf(owner, messageId);
            return;
        }
        rememberActiveTurn(owner, messageId);
        return () => {
            forgetActiveTurnIf(owner, messageId);
        };
    }, [turnStillRunning, target, owner, ownerMatchesCurrentIdentity]);

    const attachment = useTurnAttachment({
        target: enabled ? target : null,
        statusKind: recovery.statusKind,
        streamAvailable: recovery.data?.stream_available ?? false,
        successfulReadToken: recovery.successfulReadToken,
    });
    quiesceRef.current = attachment.quiesce;

    // Only offered when it can actually be honoured: the one-shot channel and the
    // parent turn must both exist, or RecoveringMessage falls back to copy.
    const onRetry =
        retryTarget !== undefined && parentId !== null
            ? () => startRecoveryRetry(aui, retryTarget, messageId, parentId)
            : undefined;

    // Abandon names the assistant message, so the id the status poll already
    // uses is the one the request needs. Each step reports separately because
    // the control has to distinguish a wait that is still running from a turn
    // the server ended behind a view that could not catch up.
    const onCancel = isDocumentWait(recovery.data)
        ? async (): Promise<DocumentWaitCancelOutcome> => {
              // A stale target or a moved identity sends no request at all, so
              // the wait is still running and the control must say so rather
              // than sit disabled over a cancel that never happened.
              if (target === null || !ownerMatchesCurrentIdentity()) {
                  return "refused";
              }
              try {
                  await abandonActiveChatTurn(
                      target.conversationId,
                      target.messageId
                  );
              } catch {
                  return "refused";
              }
              try {
                  await reloadThread(true);
              } catch {
                  // The server already accepted the abandon, so nothing here
                  // may read as a failed cancel: the turn is over and only this
                  // view is behind.
                  return "cancelled_stale";
              }
              return "cancelled";
          }
        : undefined;

    // The live row, not the frozen one: while the attachment is open the row is
    // the empty sibling and the caret owns progress, so the affordance stands
    // down until the branch is restored.
    if (!showsAffordance || !enabled) return null;

    return (
        <RecoveringMessage
            status={recovery.data}
            takingLonger={recovery.takingLonger}
            onRetry={onRetry}
            onCancel={onCancel}
            waitingHint={waitingHint}
        />
    );
};
